import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { WisetackApplicationStatus } from '@notifi/shared-types/wisetack-application-status';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { WisetackSettings } from '@/types/settings';

/**
 * Staging aid: stores the enrollment state a raw Wisetack `application_status`
 * maps to, so the panel and every downstream placement behave as if the
 * merchant report had said so. `null` means "no row in the report".
 *
 * Operations refuses this unless the provisioner turned the simulator on for
 * the tenant, so the caller is not the gate — the dropdown is simply hidden.
 */
export function useSimulateWisetackStatus() {
  const queryClient = useQueryClient();
  return useApiMutation<WisetackSettings, WisetackApplicationStatus | null>({
    mutationFn: (applicationStatus) =>
      apiPost<WisetackSettings>(operationsClient, settingsEndpoints.wisetackSimulateStatus(), {
        applicationStatus: applicationStatus ?? undefined,
      }),
    onSuccess: (settings) => {
      queryClient.setQueryData(settingsKeys.wisetackSettings(), settings);
      void queryClient.invalidateQueries({ queryKey: settingsKeys.wisetackSettings() });
      toast.success(`Enrollment simulated as ${settings.enrollmentState.replace(/_/g, ' ')}`);
    },
  });
}
